/**
 * FikoOlcumBase - Kumpas ve Cetvel icin ortak base class
 *
 * Bu abstract class, olcum araclari arasindaki kod tekrarini azaltir.
 * Ortak ozellikler, ayarlar ve metodlar burada tanimlanir.
 */

import { ChangeDetectorRef, Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoEksen } from '../FikoEksen';
import { FikoSecililer } from '../FikoSecililer';
import { FikoOlcumler } from '../FikoOlcumler';
import { FikoOlcumCizgisi } from '../FikoOlcumCizgisi';
import { FikoRaycastConfigService } from '../../services/fiko-raycast-config.service';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { gpuOlcumMotoru } from './gpu/GPUOlcumMotoru';
import { FikoGeometricSnap, FikoSnapIndicator, SnapResult } from './FikoGeometricSnap';

// v2.0.0 - Dimension Visual System
import {
  DimensionSettings,
  DimensionManager,
  DimensionAnimator,
  DimensionTooltip,
  type PresetName
} from './dimension';

/**
 * Secim turu tipleri
 */
export type SecimTuru = "Her Şey" | "Sadece Nokta" | "Sadece Çizgi" | "Sadece Yüzey" | "Sadece Gruplar" | "Nokta Koy";

/**
 * Sonuc tipi
 */
export type SonucTipi = "Nokta" | "Çizgi" | "Yüzey" | "Grup" | null;

/**
 * Secim filtre turu
 */
export type SecimFiltre = string[] | "*";

/**
 * Secim turu icin filtre haritasi
 */
export const SECIM_FILTRE_HARITASI: Record<SecimTuru, { filtre: SecimFiltre; profile: string }> = {
  "Her Şey": { filtre: "*", profile: 'EVERYTHING' },
  "Sadece Nokta": { filtre: ["Points", "FikoCizimYalin"], profile: 'POINTS_ONLY' },
  "Sadece Çizgi": { filtre: ["LineSegments", "LineSegments2", "Line2", "LineGeometry", "FikoCizimYalin"], profile: 'LINES_ONLY' },
  "Sadece Yüzey": { filtre: ["Mesh", "FikoCizimYalin"], profile: 'MESH_ONLY' },
  "Sadece Gruplar": { filtre: ["FikoEksen", "FikoGrup", "FikoCizimYalin"], profile: 'DEFAULT' },
  "Nokta Koy": { filtre: ["Nokta Koy"], profile: 'EVERYTHING' }
};

/**
 * Ortak olcum ayarlari interface'i
 */
export interface IOlcumAyarlari {
  // Highlight ayarlari
  hoverRengi: string;
  hoverOpacity: number;
  secilmisRengi: string;
  secilmisOpacity: number;
  highlightSaydamlik: boolean;
  highlightRoughness: number;
  highlightMetalness: number;
  highlightEmissiveIntensity: number;
  // Cizgi ayarlari
  cizgiHoverRengi: string;
  cizgiHoverOpacity: number;
  cizgiSecilmisRengi: string;
  cizgiSecilmisOpacity: number;
  cizgiHighlightKalinligi: number;
  // Nokta Koy ayarlari
  noktaKoyBoyutu: number;
  noktaKoyHoverRengi: string;
  // CATIA ayarlari
  cizimRengi: string;
  cizgiKalinligi: number;
  iziRengi: string;
  iziKalinligi: number;
  yaziRengi: string;
  yaziBoyutu: number;
  hassasiyet: number;
  // Olcum cizgisi ayarlari
  olcumOffset: number;
  okBoyutu: number;
  extensionLineGoster: boolean;
  merkezIsaretiGoster: boolean;
  suruklenebilir: boolean;
  olcumYaziBackgroundAlpha: number;
  // Tolerans ayarlari
  cemberTolerAnsi: number;
  cemberSapmaEsigi: number;
  minOlcum: number;
  maxOlcum: number;
  koordinatBas: number;
  // Secim ayarlari
  edgeSelectionEnabled: boolean;
  edgeSelectionPixelThreshold: number;
  pointSelectionEnabled: boolean;
  pointSelectionPixelThreshold: number;
  smartPriorityEnabled: boolean;
  surfacePriorityThreshold: number;
  edgeSelectionPriority: boolean;
}

/**
 * Varsayilan olcum ayarlari
 */
export const VARSAYILAN_OLCUM_AYARLARI: IOlcumAyarlari = {
  // Highlight ayarlari
  hoverRengi: '#00ffff',
  hoverOpacity: 0.5,
  secilmisRengi: '#ff0000',
  secilmisOpacity: 0.5,
  highlightSaydamlik: false,
  highlightRoughness: 0.3,
  highlightMetalness: 0.2,
  highlightEmissiveIntensity: 0.8,
  // Cizgi ayarlari
  cizgiHoverRengi: '#00ff00',
  cizgiHoverOpacity: 0.7,
  cizgiSecilmisRengi: '#ffff00',
  cizgiSecilmisOpacity: 0.7,
  cizgiHighlightKalinligi: 5,
  // Nokta Koy ayarlari
  noktaKoyBoyutu: 20,
  noktaKoyHoverRengi: '#FFD700',
  // CATIA ayarlari - 🎯 SİYAH RENKLER
  cizimRengi: '#000000',           // 🎯 SİYAH - çizim rengi
  cizgiKalinligi: 2,
  iziRengi: '#000000',             // 🎯 SİYAH - iz rengi
  iziKalinligi: 1,
  yaziRengi: '#000000',            // 🎯 SİYAH YAZI
  yaziBoyutu: 24,
  hassasiyet: 0.1,
  // Olcum cizgisi ayarlari
  olcumOffset: 15,
  okBoyutu: 0.8,
  extensionLineGoster: true,
  merkezIsaretiGoster: true,
  suruklenebilir: true,
  olcumYaziBackgroundAlpha: 0.5,
  // Tolerans ayarlari
  cemberTolerAnsi: 0.05,
  cemberSapmaEsigi: 20,
  minOlcum: 0.1,
  maxOlcum: 10000,
  koordinatBas: 4,
  // Secim ayarlari
  edgeSelectionEnabled: true,
  edgeSelectionPixelThreshold: 30,
  pointSelectionEnabled: true,
  pointSelectionPixelThreshold: 25,
  smartPriorityEnabled: true,
  surfacePriorityThreshold: 15,
  edgeSelectionPriority: true
};

/**
 * FikoOlcumBase - Abstract base class for measurement tools
 * @Directive decorator is required for Angular to recognize this as an injectable class
 */
@Directive()
export abstract class FikoOlcumBase implements IOlcumAyarlari, OnInit, OnDestroy {
  // ========== SUBSCRIPTIONS & EVENT MAPS ==========
  subsList: Subscription[] = [];
  haritaOlaylar: Map<string, any> = new Map();

  // ========== SELECTION SYSTEM ==========
  fikoSecim1: FikoSecililer;
  kumpasEksen: FikoEksen;
  secim1Filitre: SecimFiltre = "*";

  // ========== GPU STATE ==========
  gpuMode: string = 'CPU';
  gpuStats: any = null;

  // ========== MEASUREMENT OBJECTS ==========
  protected fikoOlcumCizgisi: FikoOlcumCizgisi | null = null;
  protected fikoOlcumlerGrubu: FikoOlcumler | null = null;

  // ========== v2.0.0 - DIMENSION VISUAL SYSTEM ==========
  protected dimensionSettings: DimensionSettings;
  protected dimensionManager: DimensionManager | null = null;

  // v2.0.0 Visual ayarları
  dimensionPreset: PresetName = 'fikoyt';
  animasyonAktif: boolean = true;
  glowAktif: boolean = true;
  tooltipAktif: boolean = true;
  animasyonSuresi: number = 200;
  glowYogunlugu: number = 0.6;

  // ========== CACHE SYSTEM ==========
  protected olcumCache: Map<string, any> = new Map();
  protected maksKacheUzunlugu: number = 150;
  protected lastMeasurementTime: number = 0;

  // ========== NOKTA KOY ==========
  protected hoverNoktaPoints: THREE.Points | null = null;
  protected kaliciNoktalar: THREE.Points[] = [];

  // ========== GEOMETRIC SNAP SYSTEM ==========
  protected geometricSnap: FikoGeometricSnap = new FikoGeometricSnap();
  protected snapAktif: boolean = true;

  // ========== HIGHLIGHT SETTINGS (from IOlcumAyarlari) ==========
  hoverRengi: string = VARSAYILAN_OLCUM_AYARLARI.hoverRengi;
  hoverOpacity: number = VARSAYILAN_OLCUM_AYARLARI.hoverOpacity;
  secilmisRengi: string = VARSAYILAN_OLCUM_AYARLARI.secilmisRengi;
  secilmisOpacity: number = VARSAYILAN_OLCUM_AYARLARI.secilmisOpacity;
  highlightSaydamlik: boolean = VARSAYILAN_OLCUM_AYARLARI.highlightSaydamlik;
  highlightRoughness: number = VARSAYILAN_OLCUM_AYARLARI.highlightRoughness;
  highlightMetalness: number = VARSAYILAN_OLCUM_AYARLARI.highlightMetalness;
  highlightEmissiveIntensity: number = VARSAYILAN_OLCUM_AYARLARI.highlightEmissiveIntensity;

  // ========== LINE SETTINGS ==========
  cizgiHoverRengi: string = VARSAYILAN_OLCUM_AYARLARI.cizgiHoverRengi;
  cizgiHoverOpacity: number = VARSAYILAN_OLCUM_AYARLARI.cizgiHoverOpacity;
  cizgiSecilmisRengi: string = VARSAYILAN_OLCUM_AYARLARI.cizgiSecilmisRengi;
  cizgiSecilmisOpacity: number = VARSAYILAN_OLCUM_AYARLARI.cizgiSecilmisOpacity;
  cizgiHighlightKalinligi: number = VARSAYILAN_OLCUM_AYARLARI.cizgiHighlightKalinligi;

  // ========== NOKTA KOY SETTINGS ==========
  noktaKoyBoyutu: number = VARSAYILAN_OLCUM_AYARLARI.noktaKoyBoyutu;
  noktaKoyHoverRengi: string = VARSAYILAN_OLCUM_AYARLARI.noktaKoyHoverRengi;

  // ========== CATIA SETTINGS ==========
  cizimRengi: string = VARSAYILAN_OLCUM_AYARLARI.cizimRengi;
  cizgiKalinligi: number = VARSAYILAN_OLCUM_AYARLARI.cizgiKalinligi;
  iziRengi: string = VARSAYILAN_OLCUM_AYARLARI.iziRengi;
  iziKalinligi: number = VARSAYILAN_OLCUM_AYARLARI.iziKalinligi;
  yaziRengi: string = VARSAYILAN_OLCUM_AYARLARI.yaziRengi;
  yaziBoyutu: number = VARSAYILAN_OLCUM_AYARLARI.yaziBoyutu;
  hassasiyet: number = VARSAYILAN_OLCUM_AYARLARI.hassasiyet;

  // ========== MEASUREMENT LINE SETTINGS ==========
  olcumOffset: number = VARSAYILAN_OLCUM_AYARLARI.olcumOffset;
  okBoyutu: number = VARSAYILAN_OLCUM_AYARLARI.okBoyutu;
  extensionLineGoster: boolean = VARSAYILAN_OLCUM_AYARLARI.extensionLineGoster;
  merkezIsaretiGoster: boolean = VARSAYILAN_OLCUM_AYARLARI.merkezIsaretiGoster;
  suruklenebilir: boolean = VARSAYILAN_OLCUM_AYARLARI.suruklenebilir;
  olcumYaziBackgroundAlpha: number = VARSAYILAN_OLCUM_AYARLARI.olcumYaziBackgroundAlpha;

  // ========== TOLERANCE SETTINGS ==========
  cemberTolerAnsi: number = VARSAYILAN_OLCUM_AYARLARI.cemberTolerAnsi;
  cemberSapmaEsigi: number = VARSAYILAN_OLCUM_AYARLARI.cemberSapmaEsigi;
  minOlcum: number = VARSAYILAN_OLCUM_AYARLARI.minOlcum;
  maxOlcum: number = VARSAYILAN_OLCUM_AYARLARI.maxOlcum;
  koordinatBas: number = VARSAYILAN_OLCUM_AYARLARI.koordinatBas;

  // ========== SELECTION SETTINGS ==========
  edgeSelectionEnabled: boolean = VARSAYILAN_OLCUM_AYARLARI.edgeSelectionEnabled;
  edgeSelectionPixelThreshold: number = VARSAYILAN_OLCUM_AYARLARI.edgeSelectionPixelThreshold;
  pointSelectionEnabled: boolean = VARSAYILAN_OLCUM_AYARLARI.pointSelectionEnabled;
  pointSelectionPixelThreshold: number = VARSAYILAN_OLCUM_AYARLARI.pointSelectionPixelThreshold;
  smartPriorityEnabled: boolean = VARSAYILAN_OLCUM_AYARLARI.smartPriorityEnabled;
  surfacePriorityThreshold: number = VARSAYILAN_OLCUM_AYARLARI.surfacePriorityThreshold;
  edgeSelectionPriority: boolean = VARSAYILAN_OLCUM_AYARLARI.edgeSelectionPriority;

  // ========== ANA GOSTERIM ==========
  protected _anaGosterim: FikoThreeJsComponent;
  public get anaGosterim(): FikoThreeJsComponent {
    return this._anaGosterim;
  }
  @Input() public set anaGosterim(v: FikoThreeJsComponent) {
    this._anaGosterim = v;
  }

  // ========== SECIM TURU ==========
  protected _secim1Turu: SecimTuru = "Her Şey";
  public get secim1Turu(): SecimTuru {
    return this._secim1Turu;
  }
  public set secim1Turu(v: SecimTuru) {
    this._secim1Turu = v;
    this.secimTuruDegisti(v, this.fikoSecim1, 'secim1Filitre');
  }

  constructor(
    public readonly FikoYT: FikoYTService,
    public readonly CD: ChangeDetectorRef,
    protected raycastConfigService: FikoRaycastConfigService
  ) {
    // v2.0.0 - DimensionSettings singleton
    this.dimensionSettings = DimensionSettings.getInstance();
  }

  /**
   * Secim turu degistiginde cagrilir
   * Alt siniflar override edebilir
   */
  protected secimTuruDegisti(
    yeniTur: SecimTuru,
    fikoSecim: FikoSecililer,
    filtreProperty: 'secim1Filitre' | 'secim2Filitre'
  ): void {
    if (!fikoSecim) return;

    fikoSecim.temizle();

    // Secim turune gore filtre ve profili al
    const config = SECIM_FILTRE_HARITASI[yeniTur];
    (this as any)[filtreProperty] = config.filtre;
    this.raycastConfigService.applyProfile(config.profile as any);

    // Nokta Koy modunda highlight'lari temizle
    if (yeniTur === "Nokta Koy") {
      this.noktaKoyModuTemizle(fikoSecim);
    }

    // Raycast params'i set et
    fikoSecim.raycaster.params = this.raycastConfigService.getRaycastParams();

    // Filtre guncelle
    if (Array.isArray((this as any)[filtreProperty]) &&
        (this as any)[filtreProperty].indexOf("Nokta Koy") > -1) {
      fikoSecim.filitreler = "*";
    } else {
      fikoSecim.filitreler = (this as any)[filtreProperty];
    }
    fikoSecim.rayObje = this.anaGosterim.sahneler[0].children;

    if (this.anaGosterim?.ciz) {
      this.anaGosterim.ciz();
    }
    this.CD.detectChanges();
  }

  /**
   * Nokta Koy modunda highlight'lari temizle
   * Alt siniflar override edebilir
   */
  protected noktaKoyModuTemizle(fikoSecim: FikoSecililer): void {
    if (fikoSecim.grupSecimler) {
      fikoSecim.grupSecimler.clear();
    }
    if (fikoSecim.grupSecimlerUzerinde) {
      fikoSecim.grupSecimlerUzerinde.clear();
    }
  }

  /**
   * Kaydedilen ayarlari yukle
   */
  loadAyarlar(): void {
    this.loadThreeJsAyarlari();
    this.loadOlcumAyarlari();
    this.CD.detectChanges();
  }

  /**
   * 🎯 Kenar yakalama hassasiyetini (raycast piksel toleransı) CANLI uygula.
   * Slider değiştikçe ayarlar dialog'undan çağrılır. Değeri doğrudan paylaşılan seçim
   * instance(ler)ine yazar; FikoSecililer raycast'i edgeSelectionPixelThreshold'u kullanır
   * → büyük değer = imleç kenara uzakken bile yakalar (kolay seçim).
   */
  public kenarHassasiyetiUygula(px: number): void {
    if (!px || px <= 0) return;
    this.edgeSelectionPixelThreshold = px;
    const s1 = (this as any).fikoSecim1;
    const s2 = (this as any).fikoSecim2;
    if (s1) s1.edgeSelectionPixelThreshold = px;
    if (s2) s2.edgeSelectionPixelThreshold = px;
  }

  /**
   * THREE.JS ayarlarini yukle (highlight, cizgi renkleri vs.)
   */
  protected loadThreeJsAyarlari(): void {
    const threeJsAyarlar = localStorage.getItem('FikoThreeJSAyarlari');
    if (!threeJsAyarlar) return;

    try {
      const parsed = JSON.parse(threeJsAyarlar);

      // Yuzey highlight ayarlari
      if (parsed.hoverRengi !== undefined) this.hoverRengi = parsed.hoverRengi;
      if (parsed.hoverOpacity !== undefined) this.hoverOpacity = parsed.hoverOpacity;
      if (parsed.secilmisRengi !== undefined) this.secilmisRengi = parsed.secilmisRengi;
      if (parsed.secilmisOpacity !== undefined) this.secilmisOpacity = parsed.secilmisOpacity;

      // Material ayarlari
      if (parsed.highlightSaydamlik !== undefined) this.highlightSaydamlik = parsed.highlightSaydamlik;
      if (parsed.highlightRoughness !== undefined) this.highlightRoughness = parsed.highlightRoughness;
      if (parsed.highlightMetalness !== undefined) this.highlightMetalness = parsed.highlightMetalness;
      if (parsed.highlightEmissiveIntensity !== undefined) this.highlightEmissiveIntensity = parsed.highlightEmissiveIntensity;

      // Cizgi highlight ayarlari
      if (parsed.cizgiHoverRengi !== undefined) this.cizgiHoverRengi = parsed.cizgiHoverRengi;
      if (parsed.cizgiHoverOpacity !== undefined) this.cizgiHoverOpacity = parsed.cizgiHoverOpacity;
      if (parsed.cizgiSecilmisRengi !== undefined) this.cizgiSecilmisRengi = parsed.cizgiSecilmisRengi;
      if (parsed.cizgiSecilmisOpacity !== undefined) this.cizgiSecilmisOpacity = parsed.cizgiSecilmisOpacity;
      if (parsed.cizgiKalinligi !== undefined) this.cizgiHighlightKalinligi = parsed.cizgiKalinligi;

      // Nokta Koy ayarlari
      if (parsed.noktaKoyBoyutu !== undefined) this.noktaKoyBoyutu = parsed.noktaKoyBoyutu;
      if (parsed.noktaKoyHoverRengi !== undefined) this.noktaKoyHoverRengi = parsed.noktaKoyHoverRengi;

      // Olcum ayarlari
      if (parsed.olcumCizgiRengi !== undefined) this.cizimRengi = parsed.olcumCizgiRengi;
      if (parsed.olcumCizgiKalinligi !== undefined) this.cizgiKalinligi = parsed.olcumCizgiKalinligi;
      if (parsed.olcumOffsetMesafe !== undefined) this.olcumOffset = parsed.olcumOffsetMesafe;
      if (parsed.olcumYaziFontSize !== undefined) this.yaziBoyutu = parsed.olcumYaziFontSize;
      if (parsed.olcumYaziBackgroundAlpha !== undefined) this.olcumYaziBackgroundAlpha = parsed.olcumYaziBackgroundAlpha;

      // Kenar/Nokta secim ayarlari
      if (parsed.kenarSecimPikselToleransi !== undefined) this.edgeSelectionPixelThreshold = parsed.kenarSecimPikselToleransi;
      if (parsed.noktaSecimPikselToleransi !== undefined) this.pointSelectionPixelThreshold = parsed.noktaSecimPikselToleransi;
      if (parsed.akillıOncelikAktif !== undefined) this.smartPriorityEnabled = parsed.akillıOncelikAktif;
      if (parsed.yuzeyOncelikEsigi !== undefined) this.surfacePriorityThreshold = parsed.yuzeyOncelikEsigi;
      if (parsed.cizgiOncelikliSecim !== undefined) this.edgeSelectionPriority = parsed.cizgiOncelikliSecim;
    } catch (e) {
      // Hata durumunda varsayilanlari kullan
    }
  }

  /**
   * Olcum ozel ayarlarini yukle
   */
  protected loadOlcumAyarlari(): void {
    // 🔴 Önce FikoThreeJSAyarlari'ndan merkezi ayarları yükle
    try {
      const threeJsAyarlari = localStorage.getItem('FikoThreeJSAyarlari');
      if (threeJsAyarlari) {
        const parsed = JSON.parse(threeJsAyarlari);
        // 🎯 Çizgi/Ok rengi merkezi ayarlardan (cizimRengi ve iziRengi için de kullan)
        if (parsed.olcumCizgiRengi !== undefined) {
          this.cizimRengi = parsed.olcumCizgiRengi;
          this.iziRengi = parsed.olcumCizgiRengi;
        }
        // Yazı rengi merkezi ayarlardan gelsin
        if (parsed.olcumYaziRengi !== undefined) {
          this.yaziRengi = parsed.olcumYaziRengi;
        }
        // Yazı boyutu merkezi ayarlardan
        if (parsed.olcumYaziFontSize !== undefined) {
          this.yaziBoyutu = parsed.olcumYaziFontSize;
        }
        // Çizgi kalınlığı merkezi ayarlardan
        if (parsed.olcumCizgiKalinligi !== undefined) {
          this.cizgiKalinligi = parsed.olcumCizgiKalinligi;
        }
      }
    } catch (e) {
      // Hata durumunda varsayilanlari kullan
    }

    // Olcum-spesifik ayarlar
    const kaydedilen = localStorage.getItem('FikoYTOlcumAyarlari');
    if (!kaydedilen) return;

    try {
      const parsed = JSON.parse(kaydedilen);

      // 🎯 Klasik uzatma çizgisi (sürüklemede paralel) seçimi → alt sınıftaki klasikUzatma bayrağı
      if (parsed.klasikOlcumStili !== undefined) (this as any).klasikUzatma = parsed.klasikOlcumStili;
      // 🎯 Kenar yakalama hassasiyeti (slider) açılışta yüklensin
      if (parsed.kenarHassasiyet !== undefined) this.edgeSelectionPixelThreshold = parsed.kenarHassasiyet;

      // Hassasiyet ayarlari
      if (parsed.hassasiyet !== undefined) this.hassasiyet = parsed.hassasiyet;
      if (parsed.minOlcum !== undefined) this.minOlcum = parsed.minOlcum;
      if (parsed.maxOlcum !== undefined) this.maxOlcum = parsed.maxOlcum;
      if (parsed.cemberTolerAnsi !== undefined) this.cemberTolerAnsi = parsed.cemberTolerAnsi;
      if (parsed.cemberSapmaEsigi !== undefined) this.cemberSapmaEsigi = parsed.cemberSapmaEsigi;

      // Goruntu ayarlari
      if (parsed.iziRengi !== undefined) this.iziRengi = parsed.iziRengi;
      if (parsed.iziKalinligi !== undefined) this.iziKalinligi = parsed.iziKalinligi;
      // yaziRengi artık FikoThreeJSAyarlari'ndan geliyor, ama eski ayar varsa onu da kullan
      if (parsed.yaziRengi !== undefined) this.yaziRengi = parsed.yaziRengi;

      // Kenar secim ayarlari
      if (parsed.edgeSelectionEnabled !== undefined) this.edgeSelectionEnabled = parsed.edgeSelectionEnabled;
      if (parsed.edgeSelectionPixelThreshold !== undefined) this.edgeSelectionPixelThreshold = parsed.edgeSelectionPixelThreshold;
      // Hassasiyet slider'ı (ayarlar dialog) → raycast piksel toleransı
      if (parsed.kenarHassasiyet !== undefined) this.edgeSelectionPixelThreshold = parsed.kenarHassasiyet;

      // Nokta secim ayarlari
      if (parsed.pointSelectionEnabled !== undefined) this.pointSelectionEnabled = parsed.pointSelectionEnabled;
      if (parsed.pointSelectionPixelThreshold !== undefined) this.pointSelectionPixelThreshold = parsed.pointSelectionPixelThreshold;

      // Akilli oncelik ayarlari
      if (parsed.smartPriorityEnabled !== undefined) this.smartPriorityEnabled = parsed.smartPriorityEnabled;
      if (parsed.surfacePriorityThreshold !== undefined) this.surfacePriorityThreshold = parsed.surfacePriorityThreshold;

      // v2.0.0 - Visual system ayarları
      if (parsed.dimensionPreset !== undefined) this.dimensionPreset = parsed.dimensionPreset;
      if (parsed.animasyonAktif !== undefined) this.animasyonAktif = parsed.animasyonAktif;
      if (parsed.glowAktif !== undefined) this.glowAktif = parsed.glowAktif;
      if (parsed.tooltipAktif !== undefined) this.tooltipAktif = parsed.tooltipAktif;
      if (parsed.animasyonSuresi !== undefined) this.animasyonSuresi = parsed.animasyonSuresi;
      if (parsed.glowYogunlugu !== undefined) this.glowYogunlugu = parsed.glowYogunlugu;

      // DimensionSettings'e uygula
      this.applyDimensionSettings();

      // Snap ayarlarını yükle
      this.loadSnapAyarlari();
    } catch (e) {
      // Hata durumunda varsayilanlari kullan
    }
  }

  /**
   * v2.0.0 - DimensionSettings'e ayarları uygula
   */
  protected applyDimensionSettings(): void {
    // Preset uygula
    this.dimensionSettings.applyPreset(this.dimensionPreset);

    // Ayarları güncelle
    this.dimensionSettings.updateSettings({
      animation: {
        enabled: this.animasyonAktif,
        duration: this.animasyonSuresi,
        fadeIn: true,
        fadeOut: true
      },
      glow: {
        enabled: this.glowAktif,
        intensity: this.glowYogunlugu,
        color: 0x000000, // 🎯 SİYAH
        onHover: true,
        onSelect: true
      },
      tooltip: {
        enabled: this.tooltipAktif,
        showDelay: 200,
        hideDelay: 100,
        backgroundColor: 'rgba(0,0,0,0.8)',
        textColor: '#ffffff',
        fontSize: 12
      }
    });
  }

  /**
   * v2.0.0 - Preset değiştir
   */
  setDimensionPreset(preset: PresetName): void {
    this.dimensionPreset = preset;
    this.dimensionSettings.applyPreset(preset);

    // DimensionManager varsa güncelle
    if (this.dimensionManager) {
      this.dimensionManager.applyPreset(preset);
    }
  }

  /**
   * v2.0.0 - Animasyon ayarlarını güncelle
   */
  updateAnimationSettings(enabled: boolean, duration?: number): void {
    this.animasyonAktif = enabled;
    if (duration !== undefined) this.animasyonSuresi = duration;

    this.dimensionSettings.updateSettings({
      animation: {
        enabled,
        duration: this.animasyonSuresi,
        fadeIn: true,
        fadeOut: true
      }
    });
  }

  /**
   * v2.0.0 - Glow ayarlarını güncelle
   */
  updateGlowSettings(enabled: boolean, intensity?: number): void {
    this.glowAktif = enabled;
    if (intensity !== undefined) this.glowYogunlugu = intensity;

    this.dimensionSettings.updateSettings({
      glow: {
        enabled,
        intensity: this.glowYogunlugu,
        color: 0x000000, // 🎯 SİYAH
        onHover: true,
        onSelect: true
      }
    });
  }

  /**
   * v2.0.0 - Tooltip ayarlarını güncelle
   */
  updateTooltipSettings(enabled: boolean): void {
    this.tooltipAktif = enabled;

    this.dimensionSettings.updateSettings({
      tooltip: {
        enabled,
        showDelay: 200,
        hideDelay: 100,
        backgroundColor: 'rgba(0,0,0,0.8)',
        textColor: '#ffffff',
        fontSize: 12
      }
    });
  }

  /**
   * v2.0.0 - DimensionManager oluştur
   */
  protected initDimensionManager(): void {
    if (!this.anaGosterim?.sahneler?.[0]) return;

    const sahne = this.anaGosterim.sahneler[0] as any;
    this.dimensionManager = new DimensionManager(sahne);

    // Mevcut ayarları uygula
    this.dimensionManager.applyPreset(this.dimensionPreset);
    this.dimensionManager.loadSettings();
  }

  /**
   * GPU Olcum Motoru'nu baslat
   */
  protected async initGPUOlcumMotoru(): Promise<void> {
    if (!this.anaGosterim?.sunucu) return;
    await gpuOlcumMotoru.initialize(this.anaGosterim.sunucu);
    gpuOlcumMotoru.optimizeScene(this.anaGosterim.sahneler[0]);
    this.gpuStats = gpuOlcumMotoru.getPerformanceStats();
    this.gpuMode = this.gpuStats.mode;
  }

  /**
   * FikoOlcumler grubunu olustur veya var olani bul
   */
  protected initOlcumlerGrubu(): void {
    const sahneFiko = this.anaGosterim.sahneler[0] as any;
    this.fikoOlcumlerGrubu = sahneFiko.getObjectByName('Ölçümler') as FikoOlcumler;

    if (!this.fikoOlcumlerGrubu) {
      this.fikoOlcumlerGrubu = new FikoOlcumler((sahneFiko.sahne || undefined) as any);
      sahneFiko.add(this.fikoOlcumlerGrubu);
    }
  }

  /**
   * FikoSecililer'i ayarla (ortak ayarlar)
   */
  protected setupFikoSecililer(fikoSecim: FikoSecililer): void {
    fikoSecim.temizle();
    fikoSecim.aktif = true;
    fikoSecim.kaliciSecimModu = true;

    // 🎯 EDGE MIKNATISI: Ekran-uzayı piksel tabanlı kenar/nokta yakalamayı AÇ → kenarlar imlece
    // uzakken bile yakalanır (CATIA tarzı kolay seçim). Eşik kullanıcı slider'ından gelir.
    // (Eskiden hepsi false'tu → kenar seçimi çok zordu.)
    fikoSecim.pikselTabanliSecimAktif = true;
    fikoSecim.edgeSelectionEnabled = true;
    fikoSecim.pointSelectionEnabled = true;
    fikoSecim.smartPriorityEnabled = false;
    fikoSecim.edgeSelectionPriority = true;
    (fikoSecim as any).kenarSecimAktif = true; // hover'da da kenar yakala
    if (this.edgeSelectionPixelThreshold) fikoSecim.edgeSelectionPixelThreshold = this.edgeSelectionPixelThreshold;
    fikoSecim.useGPUPicking = true;
    (fikoSecim as any).highlightThrottleMs = 0;

    // 🎯 KENAR INDEX TAZELE: Spatial index init'te (model yüklenmeden) boş kayıt olmuş olabilir →
    // ölçüm açılışında (model artık sahnede) yeniden kaydet ki kenarlar indekslensin (TIER1 dolu olsun).
    try { (fikoSecim as any).registerEdgesForSpatialIndex?.(); } catch (e) { /* sessiz */ }

    // Highlight renklerini ayarla
    this.applyHighlightColors(fikoSecim);
  }

  /**
   * Highlight renklerini FikoSecililer'e uygula
   */
  protected applyHighlightColors(fikoSecim: FikoSecililer): void {
    const hoverRenk = parseInt(this.hoverRengi.replace('#', ''), 16);
    const secilmisRenk = parseInt(this.secilmisRengi.replace('#', ''), 16);
    fikoSecim.hoverColor = new THREE.Color(hoverRenk);
    fikoSecim.selectedColor = new THREE.Color(secilmisRenk);
    fikoSecim.hoverOpacity = this.hoverOpacity;
    fikoSecim.selectedOpacity = this.secilmisOpacity;

    const cizgiHoverRenk = parseInt(this.cizgiHoverRengi.replace('#', ''), 16);
    const cizgiSecilmisRenk = parseInt(this.cizgiSecilmisRengi.replace('#', ''), 16);
    fikoSecim.cizgiHoverColor = new THREE.Color(cizgiHoverRenk);
    fikoSecim.cizgiSecilmisColor = new THREE.Color(cizgiSecilmisRenk);
    fikoSecim.cizgiKalinligi = this.cizgiHighlightKalinligi;
    fikoSecim.highlightSaydamlik = this.highlightSaydamlik;
  }

  /**
   * Kalici noktalari temizle
   */
  protected kaliciNoktalariTemizle(): void {
    for (const nokta of this.kaliciNoktalar) {
      if (nokta.parent) {
        nokta.parent.remove(nokta);
      }
      nokta.geometry?.dispose();
      if (nokta.material) {
        if (Array.isArray(nokta.material)) {
          nokta.material.forEach(m => m.dispose());
        } else {
          nokta.material.dispose();
        }
      }
    }
    this.kaliciNoktalar = [];
  }

  /**
   * Cache'i temizle
   */
  protected clearCache(): void {
    this.olcumCache.clear();
  }

  /**
   * Subscription'lari temizle
   */
  protected clearSubscriptions(): void {
    for (const sub of this.subsList) {
      sub.unsubscribe();
    }
    this.subsList = [];
  }

  /**
   * Olay handler'larini temizle
   */
  protected clearOlaylar(): void {
    this.haritaOlaylar.forEach((handler, key) => {
      // Olay handler'ini kaldir
    });
    this.haritaOlaylar.clear();
  }

  /**
   * Hover ayarlarini yukle (override edilebilir)
   */
  protected hoverAyarlariYukle(): void {
    // Alt siniflar override edebilir
  }

  // ==================== GEOMETRIC SNAP HELPERS ====================

  /**
   * Snap sonucunu uygula: hitPoint'i snap konumuna tasir ve gosterge gunceller
   * @returns snap uygulanmis konum (snap yoksa orijinal hitPoint)
   */
  protected applySnap(hitPoint: THREE.Vector3, kesisim: any): SnapResult {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) {
      return { snapped: false, position: hitPoint, type: 'none', distance: Infinity };
    }

    const renderer = this.anaGosterim.sunucu;
    const camera = this.anaGosterim.aktifKamera;
    const w = renderer.domElement.clientWidth;
    const h = renderer.domElement.clientHeight;

    const result = this.geometricSnap.detect(hitPoint, kesisim, camera, w, h);

    // Snap gostergesini guncelle
    this.updateSnapIndicator(result, camera, w, h, renderer.domElement);

    return result;
  }

  /**
   * Snap gostergesini ekranda guncelle (HTML overlay)
   */
  /** Imlec yaninda "Kose"/snap baklava gostergesi gosterilsin mi? Kullanici istemedi → kapali. */
  protected snapGostergeGoster: boolean = false;

  protected updateSnapIndicator(
    snapResult: SnapResult,
    camera: THREE.Camera,
    width: number,
    height: number,
    canvas: HTMLElement
  ): void {
    const indicator = this.geometricSnap.indicator;

    // 🎯 Kullanici istegi: imlec yaninda "Kose" yazisi/baklava CIKMASIN. Snap'in kendisi
    // (hitPoint'in kose/kenar/merkeze yapismasi) calismaya devam eder; sadece GORSEL gosterge gizli.
    if (!this.snapGostergeGoster) {
      indicator.hide();
      return;
    }

    // Indicator henuz DOM'a eklenmemisse, canvas parent'ina ekle
    if (!indicator.isVisible() && snapResult.snapped) {
      const container = canvas.parentElement;
      if (container) {
        indicator.attach(container);
      }
    }

    if (snapResult.snapped && snapResult.type !== 'none') {
      const screen = FikoSnapIndicator.projectToScreen(snapResult.position, camera, width, height);
      if (screen) {
        indicator.update(screen.x, screen.y, snapResult);
      } else {
        indicator.hide();
      }
    } else {
      indicator.hide();
    }
  }

  /**
   * Snap gostergesini gizle (hover disinda cagrilir)
   */
  protected hideSnapIndicator(): void {
    this.geometricSnap.indicator.hide();
  }

  /**
   * Snap ayarlarını localStorage'dan yükle
   */
  protected loadSnapAyarlari(): void {
    try {
      const kaydedilen = localStorage.getItem('FikoYTSnapAyarlari');
      if (!kaydedilen) return;
      const parsed = JSON.parse(kaydedilen);
      if (parsed.enabled !== undefined) this.snapAktif = parsed.enabled;
      this.geometricSnap.updateConfig({
        enabled: this.snapAktif,
        ...parsed
      });
    } catch (e) {
      // Varsayılanları kullan
    }
  }

  /**
   * Snap ayarlarını kaydet
   */
  protected saveSnapAyarlari(): void {
    try {
      const config = this.geometricSnap.getConfig();
      localStorage.setItem('FikoYTSnapAyarlari', JSON.stringify(config));
    } catch (e) { /* silent */ }
  }

  /**
   * Snap'ı aç/kapa
   */
  toggleSnap(enabled?: boolean): void {
    this.snapAktif = enabled !== undefined ? enabled : !this.snapAktif;
    this.geometricSnap.setEnabled(this.snapAktif);
    this.saveSnapAyarlari();
  }

  /**
   * Abstract metodlar - alt siniflar implement etmeli
   */
  abstract ngOnInit(): void;
  abstract baslat(anaGosterim: FikoThreeJsComponent): Promise<void>;
  abstract bitir(): void;
  abstract ngOnDestroy(): void;
}
